import styled from '@emotion/styled'
import { type ReactNode, useId } from 'react'

export interface FormFieldProps {
  label: string
  /** Optional helper text, wired to the control via aria-describedby. */
  hint?: string
  /** Optional error message; when set, the field is marked invalid. */
  error?: string
  /** Render-prop receiving the ids to wire onto the control for a11y. */
  children: (ids: { id: string; describedBy?: string }) => ReactNode
  htmlFor?: string
}

// FormField is the accessible label + control + hint/error wrapper every studio
// form uses (KAT-297). It owns the id wiring so the control is always labelled
// and any hint/error is announced via aria-describedby.
export function FormField({ label, hint, error, children, htmlFor }: FormFieldProps) {
  const autoId = useId()
  const id = htmlFor ?? autoId
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined

  return (
    <Field>
      <Label htmlFor={id}>{label}</Label>
      {children({ id, describedBy })}
      {hint && !error && (
        <Hint id={hintId}>{hint}</Hint>
      )}
      {error && (
        <ErrorText id={errorId} role="alert">
          {error}
        </ErrorText>
      )}
    </Field>
  )
}

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: var(--c-fg);
`

const Hint = styled.span`
  font-size: 12px;
  color: var(--c-fg-subtle);
  line-height: 1.5;
`

const ErrorText = styled.span`
  font-size: 12px;
  color: var(--c-danger);
  line-height: 1.5;
`
